import {
  type AnnulOperation,
  type BranchChangeset,
  type ConfigurationOperation,
  isAnnulOperation,
  isEnumDefinitionCreateOperation,
  isEnumLiteralCreateOperation,
  isGlMapCreateOperation,
  isGlMapHideOperation,
  isLargeOperation,
  isParameterDefinitionCreateOperation,
  isParameterValueAmendOperation,
  isParameterValueCreateOperation,
  isParameterValueHideOperation,
  isReconciliationSpecCreateOperation,
  isRegisteredTenantCreateOperation,
  isTemplateCreateOperation,
  isTemplateHideOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'

import { userActionsForOperation } from '../helpers'
import type { OperationWithSourceInfo, WorkspaceOperationOf } from '../types'

const tenantBlockKey = ({
  tenantId,
  block,
}: {
  tenantId: Optional<string>
  block: Optional<string>
}) => `${tenantId ?? ''}-${block ?? ''}`

export const indexChangesetOperations = (changesets: BranchChangeset[]) => {
  const indexedOperations = new Map<string, Map<string, number>>()

  changesets.forEach((branchChangeset) => {
    const tenantId = branchChangeset.changeset?.tenantId
    const block = branchChangeset.block

    const key = tenantBlockKey({ tenantId, block })

    const existingChangesetOperationIndexes =
      indexedOperations.get(key) ?? new Map<string, number>()
    indexedOperations.set(key, existingChangesetOperationIndexes)

    const maxChangesetIndex = existingChangesetOperationIndexes?.size
      ? Math.max(
          ...Array.from(existingChangesetOperationIndexes?.values() ?? []),
        )
      : -1
    const nextChangesetIndex = maxChangesetIndex + 1

    branchChangeset.changeset?.operations
      ?.filter(notEmpty)
      .forEach((operation) => {
        if (operation.operationKey) {
          existingChangesetOperationIndexes.set(
            operation.operationKey,
            nextChangesetIndex,
          )
        }
      })
  })

  return indexedOperations
}

export const indexAnnulmentOperations = ({
  changesets,
  baseBlock,
}: {
  changesets: BranchChangeset[]
  baseBlock: string
}) => {
  const indexedAnnulments = new Map<
    string,
    Map<string, OperationWithSourceInfo<AnnulOperation>[]>
  >()

  changesets.forEach((branchChangeset) => {
    branchChangeset.changeset?.operations
      ?.filter(notEmpty)
      .forEach((operation) => {
        if (
          isAnnulOperation(operation) &&
          operation.createOperationClassName &&
          operation.name
        ) {
          const annulmentsForClass =
            indexedAnnulments.get(operation.createOperationClassName) ??
            new Map<string, OperationWithSourceInfo<AnnulOperation>[]>()
          indexedAnnulments.set(
            operation.createOperationClassName,
            annulmentsForClass,
          )

          const annulmentWithSourceInfo = {
            internalOperation: operation,
            block: branchChangeset.block ?? baseBlock,
            layer: new LayerKey({
              tenantId: branchChangeset.changeset?.tenantId,
              layerId: branchChangeset.changeset?.layer,
            }),
            isDraft: false,
            changesetHistory: branchChangeset.changesetHistory,
            actions: userActionsForOperation(
              operation,
              branchChangeset.changesetHistory,
            ),
            annulOperations: [], // Annulments cannot be annulled, so this will always be an empty array
          } satisfies OperationWithSourceInfo<AnnulOperation>

          const annulmentsForName = annulmentsForClass.get(operation.name) ?? []
          annulmentsForName.push(annulmentWithSourceInfo)
          annulmentsForClass.set(operation.name, annulmentsForName)
        }
      })
  })

  return indexedAnnulments
}

export const findActiveAnnulments = ({
  operation,
  changesetAnnulments,
  operationIndexes,
  workspaceAnnulments,
}: {
  operation: Omit<
    OperationWithSourceInfo<ConfigurationOperation>,
    'annulOperations'
  >
  changesetAnnulments: Map<
    string,
    Map<string, OperationWithSourceInfo<AnnulOperation>[]>
  >
  operationIndexes: Map<string, Map<string, number>>
  workspaceAnnulments: WorkspaceOperationOf<AnnulOperation>[]
}): OperationWithSourceInfo<AnnulOperation>[] => {
  const operationBlock = operation.block
  const operationTenant = operation.layer.tenantId
  const workspaceAnnulmentsForTenantAndBlock = workspaceAnnulments.filter(
    (annulment) =>
      annulment.block === operationBlock &&
      annulment.tenantId == operationTenant,
  )

  const operationTenantBlockKey = tenantBlockKey({
    tenantId: operationTenant,
    block: operationBlock,
  })
  const operationChangesetIndex = operation.internalOperation.operationKey
    ? (operationIndexes
        .get(operationTenantBlockKey)
        ?.get(operation.internalOperation.operationKey) ?? -1)
    : -1

  if (operationChangesetIndex === -1) {
    // If the operation is not found in any changeset it must be a workspace operation so it won't have any annulments.
    return []
  }

  const allChangesetAnnulments = (
    changesetAnnulments
      ?.get(getOperationTypeForAnnulment(operation.internalOperation))
      ?.get(createOperationName(operation.internalOperation))
      ?.filter(
        (annulmentOperation) =>
          annulmentOperation.block === operationBlock &&
          annulmentOperation.layer.tenantId === operationTenant,
      ) ?? []
  ).filter(
    (annulment) =>
      (operationIndexes
        .get(operationTenantBlockKey)
        ?.get(annulment.internalOperation.operationKey ?? '') ?? -1) >
      operationChangesetIndex,
  )

  const workspaceAnnulmentsForOperation = workspaceAnnulmentsForTenantAndBlock
    .filter(
      ({ operation: annulmentOperation }) =>
        annulmentOperation.name ===
          createOperationName(operation.internalOperation) &&
        annulmentOperation.createOperationClassName ===
          getOperationTypeForAnnulment(operation.internalOperation),
    )
    .map(
      (workspaceAnnulment) =>
        ({
          changesetHistory: undefined,
          actions: [],
          annulOperations: [], // Annulments cannot be annulled, so this will always be an empty array
          internalOperation: workspaceAnnulment.operation,
          block: operationBlock,
          isDraft: true,
          layer: new LayerKey({
            tenantId: workspaceAnnulment.tenantId,
            layerId: workspaceAnnulment.layer,
          }),
        }) satisfies OperationWithSourceInfo<AnnulOperation>,
    )

  return [...allChangesetAnnulments, ...workspaceAnnulmentsForOperation]
}

/**
 * Builds a flat index of all annul operations (both committed and draft) by their operationKey.
 * Draft operations override committed ones when keys collide.
 */
export const buildAnnulOperationsByKey = ({
  changesets,
  draftAnnulOperations,
  rootBlock,
}: {
  changesets: BranchChangeset[]
  draftAnnulOperations: WorkspaceOperationOf<AnnulOperation>[]
  rootBlock: string
}): Map<string, OperationWithSourceInfo<AnnulOperation>> => {
  const map = new Map<string, OperationWithSourceInfo<AnnulOperation>>()

  changesets.forEach((branchChangeset) => {
    branchChangeset.changeset?.operations
      ?.filter(notEmpty)
      .forEach((operation) => {
        if (isAnnulOperation(operation) && operation.operationKey) {
          map.set(operation.operationKey, {
            isDraft: false,
            block: branchChangeset.block ?? rootBlock,
            layer: new LayerKey({
              tenantId: branchChangeset.changeset?.tenantId,
              layerId: branchChangeset.changeset?.layer,
            }),
            actions: userActionsForOperation(
              operation,
              branchChangeset.changesetHistory,
            ),
            changesetHistory: branchChangeset.changesetHistory,
            annulOperations: [],
            internalOperation: operation,
          })
        }
      })
  })

  draftAnnulOperations.forEach((workspaceOperation) => {
    const internalOperation = workspaceOperation.operation
    if (internalOperation.operationKey) {
      map.set(internalOperation.operationKey, {
        isDraft: true,
        block: workspaceOperation.block ?? rootBlock,
        layer: new LayerKey({
          tenantId: workspaceOperation.tenantId,
          layerId: workspaceOperation.layer,
        }),
        actions: [],
        changesetHistory: undefined,
        annulOperations: [],
        internalOperation,
      })
    }
  })

  return map
}

export const createOperationName = (
  operation: ConfigurationOperation,
): string => {
  if (
    isTemplateCreateOperation(operation) ||
    isParameterDefinitionCreateOperation(operation) ||
    isParameterValueCreateOperation(operation) ||
    isParameterValueAmendOperation(operation) ||
    isEnumDefinitionCreateOperation(operation)
  ) {
    assertNotEmpty(operation.entity?.name)
    return operation.entity.name
  } else if (
    isTemplateHideOperation(operation) ||
    isParameterValueHideOperation(operation)
  ) {
    assertNotEmpty(operation.name)
    return operation.name
  } else if (isEnumLiteralCreateOperation(operation)) {
    assertNotEmpty(operation.entity?.name)
    assertNotEmpty(operation.entity?.enumName)
    return `${operation.entity.enumName}.${operation.entity.name}`
  } else if (isRegisteredTenantCreateOperation(operation)) {
    assertNotEmpty(operation.registeredTenant?.tenantId)
    return operation.registeredTenant.tenantId
  } else if (isGlMapCreateOperation(operation)) {
    assertNotEmpty(operation.glMap?.mappingId)
    return operation.glMap.mappingId
  } else if (isGlMapHideOperation(operation)) {
    assertNotEmpty(operation.mappingId)
    return operation.mappingId
  } else if (isReconciliationSpecCreateOperation(operation)) {
    assertNotEmpty(operation.reconciliationSpec?.specId)
    return operation.reconciliationSpec.specId
  } else if (isLargeOperation(operation)) {
    assertNotEmpty(operation.innerOperationName)
    return operation.innerOperationName
  }

  throw new Error(
    `Unsupported operation type for annulment: ${operation._type}`,
  )
}

/**
 * The create operation class an annulment of the given operation carries. Operations that
 * modify an existing item rather than create one are annulled through the class of the
 * operation they modify: a template hide through `TemplateCreateOperation`, a GL-map hide
 * through `GlMapCreateOperation`, and a parameter value amendment through
 * `ParameterValueCreateOperation`, so that one annulment covers a parameter's values and
 * the amendments applied on top of them.
 */
export const getOperationTypeForAnnulment = (
  operation: ConfigurationOperation,
): ConfigurationOperation['_type'] => {
  if (isTemplateHideOperation(operation)) {
    return 'TemplateCreateOperation'
  } else if (isGlMapHideOperation(operation)) {
    return 'GlMapCreateOperation'
  } else if (
    isParameterValueAmendOperation(operation) ||
    (isLargeOperation(operation) &&
      // Currently only parameter value create operations can be large operations.
      operation.innerOperationType === 'parameterValueCreate')
  ) {
    return 'ParameterValueCreateOperation'
  }

  return operation._type
}
